import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle, ArrowRight, Upload, Shield, Zap, Crown,
  Star, Loader2, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK_COMPARISON = [
  ['Taille max par fichier', '20 Go', 'Illimitee', 'Illimitee + P2P'],
  ['Nombre de fichiers', '5 fichiers', 'Illimite', 'Illimite'],
  ['Duree d\'expiration', '7 jours', 'Personnalisable', '60 jours'],
  ['Protection mot de passe', '\u2713', '\u2713', '\u2713'],
  ['Programmation d\'envoi', '\u2713', '\u2713', '\u2713'],
  ['QR Code', '\u2713', '\u2713', '\u2713'],
  ['Chiffrement E2E', '\u2713', '\u2713', '\u2713'],
  ['Stockage France / Europe', '\u2713', '\u2713', '\u2713'],
  ['Notifications email', '\u2014', '\u2713', '\u2713'],
  ['Statistiques detaillees', '\u2014', '\u2713', '\u2713'],
  ['Fond personnalise', '\u2014', '\u2713', '\u2713'],
  ['Vente de fichiers', '\u2014', '\u2713', '\u2713'],
  ['Transfert P2P (WebRTC)', '\u2014', '\u2014', '\u2713'],
  ['Support prioritaire', '\u2014', '\u2713', '\u2713'],
];

const COMPETITORS = [
  {
    name: 'SENDBOX',
    highlight: true,
    features: {
      'Taille max': 'Illimitee (P2P)',
      'Stockage': 'France / Europe',
      'Chiffrement E2E': true,
      'Protection mot de passe': true,
      'QR Code': true,
      'Personnalisation': true,
      'Conformite RGPD': true,
      'Vente de fichiers': true,
      'Programmation d\'envoi': true,
      'Transfert P2P': true,
      'Sans publicite (Pro)': true,
    }
  },
  {
    name: 'WeTransfer',
    features: {
      'Taille max': '2 Go (gratuit)',
      'Stockage': 'Pays-Bas / USA',
      'Chiffrement E2E': false,
      'Protection mot de passe': 'Pro uniquement',
      'QR Code': false,
      'Personnalisation': 'Pro uniquement',
      'Conformite RGPD': true,
      'Vente de fichiers': false,
      'Programmation d\'envoi': false,
      'Transfert P2P': false,
      'Sans publicite (Pro)': 'Pro uniquement',
    }
  },
  {
    name: 'Google Drive',
    features: {
      'Taille max': '15 Go (espace total)',
      'Stockage': 'USA / Global',
      'Chiffrement E2E': false,
      'Protection mot de passe': false,
      'QR Code': false,
      'Personnalisation': false,
      'Conformite RGPD': 'Partiel',
      'Vente de fichiers': false,
      'Programmation d\'envoi': false,
      'Transfert P2P': false,
      'Sans publicite (Pro)': false,
    }
  }
];

const ComparatifPage = () => {
  const [comparison, setComparison] = useState(FALLBACK_COMPARISON);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCMS = async () => {
      try {
        const res = await axios.get(`${API}/pages/features`);
        const page = res.data;
        for (const section of page.sections || []) {
          if (section.type === 'comparison' && section.rows?.length) {
            const rows = section.rows;
            // Ensure P2P row is always present with Ultimate differentiation
            const hasP2PRow = rows.some(r => r[0]?.toLowerCase().includes('p2p'));
            if (!hasP2PRow) {
              rows.push(['Transfert P2P (WebRTC)', '\u2014', '\u2014', '\u2713']);
            }
            // Ensure all rows have 4 columns (add Ultimate column if missing)
            const fixedRows = rows.map(r => r.length < 4 ? [...r, r[2]] : r);
            setComparison(fixedRows);
          }
        }
      } catch {}
      setLoading(false);
    };
    fetchCMS();
  }, []);

  const renderCell = (val) => {
    if (val === true) return <CheckCircle className="w-5 h-5 text-cyan-400 mx-auto" />;
    if (val === false) return <X className="w-5 h-5 text-red-400/50 mx-auto" />;
    return <span>{val}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900">
      <NavigationMenu />
      <main className="pt-24 pb-32">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-300 text-sm mb-6">
            <Star className="w-4 h-4" />
            Comparatif
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="comparatif-title">
            Pourquoi choisir<br />
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              SENDBOX ?
            </span>
          </h1>
          <p className="text-lg md:text-xl text-blue-200/70 max-w-3xl mx-auto">
            Comparez les fonctionnalites et decouvrez pourquoi SENDBOX est la meilleure solution pour partager vos fichiers en toute securite depuis la France.
          </p>
        </section>

        {/* Gratuit vs Pro vs Ultimate Comparison */}
        <section className="max-w-5xl mx-auto px-6 mb-20">
          <h2 className="text-3xl font-bold text-white text-center mb-10">Gratuit vs Pro vs Ultimate</h2>
          <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl overflow-hidden">
            <table className="w-full" data-testid="comparatif-table-plans">
              <thead>
                <tr className="border-b border-blue-500/20">
                  <th className="text-left p-4 text-blue-300/70 font-medium">Fonctionnalite</th>
                  <th className="text-center p-4 text-white font-medium">Gratuit</th>
                  <th className="text-center p-4 text-cyan-400 font-medium">
                    <span className="flex items-center justify-center gap-2"><Crown className="w-4 h-4 text-yellow-400" /> Pro</span>
                  </th>
                  <th className="text-center p-4 text-purple-400 font-medium">
                    <span className="flex items-center justify-center gap-2"><Zap className="w-4 h-4 text-purple-400" /> Ultimate</span>
                  </th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {comparison.map((row, idx) => (
                  <tr key={idx} className="border-b border-blue-500/10 last:border-0">
                    <td className="p-4 text-blue-200/80">{row[0]}</td>
                    <td className="p-4 text-center text-blue-300/60">{row[1]}</td>
                    <td className="p-4 text-center text-cyan-300">{row[2]}</td>
                    <td className="p-4 text-center text-purple-300">{row[3] || row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* SENDBOX vs Concurrents */}
        <section className="max-w-5xl mx-auto px-6 mb-20">
          <h2 className="text-3xl font-bold text-white text-center mb-10">SENDBOX vs la concurrence</h2>
          <div className="bg-slate-800/50 border border-blue-500/20 rounded-2xl overflow-hidden">
            <table className="w-full" data-testid="comparatif-table-competitors">
              <thead>
                <tr className="border-b border-blue-500/20">
                  <th className="text-left p-4 text-blue-300/70 font-medium">Critere</th>
                  {COMPETITORS.map((c, i) => (
                    <th key={i} className={`text-center p-4 font-medium ${c.highlight ? 'text-cyan-400' : 'text-white/80'}`}>
                      {c.highlight && <Crown className="w-4 h-4 text-yellow-400 inline mr-1" />}
                      {c.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-sm">
                {Object.keys(COMPETITORS[0].features).map((key, idx) => (
                  <tr key={idx} className="border-b border-blue-500/10 last:border-0">
                    <td className="p-4 text-blue-200/80">{key}</td>
                    {COMPETITORS.map((c, i) => (
                      <td key={i} className={`p-4 text-center ${c.highlight ? 'text-cyan-300' : 'text-blue-300/60'}`}>
                        {renderCell(c.features[key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* France/Europe Storage Banner */}
        <section className="max-w-4xl mx-auto px-6 mb-20">
          <div className="bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border border-blue-500/20 rounded-2xl p-8 flex items-center gap-6">
            <div className="flex-shrink-0 w-16 h-16 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-blue-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-1" data-testid="comparatif-france-storage">Vos fichiers sont stockes en France et en Europe</h3>
              <p className="text-blue-200/70 text-sm leading-relaxed">
                Contrairement a la plupart des services concurrents, SENDBOX heberge tous vos fichiers sur des serveurs situes en France et en Europe, garantissant une conformite totale avec le RGPD et la souverainete numerique de vos donnees.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border border-cyan-500/30 rounded-3xl p-10">
            <h2 className="text-3xl font-bold text-white mb-4">Convaincu ?</h2>
            <p className="text-blue-200/70 mb-8 max-w-xl mx-auto">Commencez gratuitement, sans inscription. Passez Pro quand vous en avez besoin.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/"><Button size="lg" className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"><Upload className="w-5 h-5 mr-2" />Envoyer des fichiers</Button></Link>
              <Link to="/pricing"><Button size="lg" variant="outline" className="border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/10">Voir les tarifs Pro<ArrowRight className="w-5 h-5 ml-2" /></Button></Link>
            </div>
          </div>
        </section>
      </main>
      <DynamicFooter fixed={true} />
    </div>
  );
};

export default ComparatifPage;
